import {
  HttpClient,
  HttpErrorResponse,
  HttpEventType,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { inject, Service } from '@angular/core';

import { catchError, delay, filter, map, Observable, of } from 'rxjs';

import { FileService } from '@shared/services/file.service';
import { type ZipProgressEvent } from '@shared/types';

import environment from '../../../../environments/environment';

import {
  DashboardPinnedItem,
  DashboardRequestPinnedItemParams,
  PinnedItem,
  PinnedItemEntity,
  SubmissionStatuses,
  DashboardWaitingOnYouRequestParams,
  DashboardWaitingOnYouRequestParamsV2,
  DashboardWaitingOnYouResponse,
  OutcomeDocumentsCheckParamsV2,
  OutcomeDocumentsCheckResponseV2,
  OutcomeDownloadEvent,
  OutcomeReadyResponse,
  OutcomeReadySubmission,
  OutcomeReadyRequestParams,
  PickUpRequestParams,
  PickUpYouResponse,
  WaitingOnYouRowsResponseV2,
  WaitingOnYouSeeDetailsCheckParamsV2,
  WaitingOnYouSeeDetailsCheckResponseV2,
  WaitingOnYouTodoCheckParamsV2,
  WaitingOnYouTodoCheckResponseV2,
} from '../types';
import {
  buildOutcomeZipFileName,
  classifyDownloadFailure,
  toDownloadPercent,
} from '../utils';
import {
  DASHBOARD_MOCK_DELAY_MS,
  OUTCOME_READY_DEFAULT_REQUEST_PARAMS,
} from '../constants';
import {
  paginateOutcomeReadyMock,
  paginateWaitingOnYouMockV2,
  PICK_UP_MOCK,
  PINNED_SITES_MOCK,
  PINNED_STUDIES_MOCK,
  SUBMISSION_STATUSES_MOCK,
  WAITING_ON_YOU_MOCK,
} from '../mocks';

@Service()
export class DashboardService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _fileService = inject(FileService);

  /**
   * Fetches the submission-status buckets for the dashboard donut card.
   *
   * @param useMock - When true, returns the typed mock payload with simulated
   * latency instead of calling the API — driven by the dashboard's floating
   * config panel.
   * @returns Observable of the submission statuses payload.
   */
  getSubmissionStatuses(useMock = false): Observable<SubmissionStatuses> {
    if (useMock) {
      return of(SUBMISSION_STATUSES_MOCK).pipe(delay(DASHBOARD_MOCK_DELAY_MS));
    }
    const _url = `${environment.irisApi}/dashboard/submission-status/`;
    return this._httpClient.get<SubmissionStatuses>(_url);
  }

  /**
   * @NOTE:- MY PINNED ITEMS
   */
  /**
   * Fetches the user's pinned studies/sites for the dashboard pinned card.
   *
   * @param params - Entity type, paging, and sort parameters for the request.
   * @param useMock - When true, returns the typed mock payload matching
   * `params.entity_type` (site vs study) with simulated latency instead of
   * calling the API — driven by the dashboard's floating config panel.
   * @returns Observable of the pinned item's payload.
   */
  getMyPinnedItems(
    params: DashboardRequestPinnedItemParams,
    useMock = false,
  ): Observable<DashboardPinnedItem> {
    if (useMock) {
      return of(
        params.entity_type === 'site' ? PINNED_SITES_MOCK : PINNED_STUDIES_MOCK,
      ).pipe(delay(DASHBOARD_MOCK_DELAY_MS));
    }
    const _url = `${environment.irisApi}/dashboard/pinned/`;
    const _params = this._buildParams<DashboardRequestPinnedItemParams>(params);

    return this._httpClient.get<DashboardPinnedItem>(_url, {
      params: _params,
    });
  }

  pinUnPinItem(
    item: Pick<PinnedItemEntity, 'id' | 'entity_type'>,
  ): Observable<PinnedItem> {
    const _url = `${environment.irisApi}/dashboard/pinned/`;
    return this._httpClient.put<PinnedItem>(_url, {
      entity_id: item.id,
      entity_type: item.entity_type,
    });
  }

  /**
   *
   * @NOTE: WAITING ON YOU
   */
  /**
   * Fetches the "Waiting on you" to-do items for the dashboard card.
   *
   * @param params - Filter and paging parameters for the request.
   * @param useMock - When true, returns the typed mock payload with simulated
   * latency instead of calling the API — driven by the dashboard's floating
   * config panel.
   * @returns Observable of the waiting-on-you payload.
   */
  getWaitingOnYouItems(
    params: DashboardWaitingOnYouRequestParams,
    useMock = false,
  ): Observable<DashboardWaitingOnYouResponse> {
    if (useMock) {
      return of(WAITING_ON_YOU_MOCK).pipe(delay(DASHBOARD_MOCK_DELAY_MS));
    }
    const _url = `${environment.irisApi}/dashboard/waiting-on-you/`;
    const _params = this._buildParams(params);
    return this._httpClient.get<DashboardWaitingOnYouResponse>(_url, {
      params: _params,
    });
  }

  /**
   * Fetches the redesigned "Waiting on you" rows (flag `waiting_on_you` on).
   *
   * @NOTE: PICK UP WHERE YOU LEFT
   */
  /**
   * Fetches the "Pick up where you left" to-do items for the dashboard card.
   *
   * @param params - Limit parameters for the request;
   * @param useMock - When true, returns the typed mock payload with simulated
   * latency instead of calling the API — driven by the dashboard's floating
   * config panel.
   * @returns Observable of the pick-up payload.
   */
  getPickUpWhereyouLeft(
    params: PickUpRequestParams,
    useMock = false,
  ): Observable<PickUpYouResponse> {
    if (useMock) {
      return of(PICK_UP_MOCK).pipe(delay(DASHBOARD_MOCK_DELAY_MS));
    }
    const _url = `${environment.irisApi}/dashboard/pick-up/`;
    const _params = this._buildParams(params);
    return this._httpClient.get<PickUpYouResponse>(_url, {
      params: _params,
    });
  }
  /**
   *
   * @param params - Filter (`all | study | site`) and paging parameters.
   * @param useMock - When true, returns the typed mock rows narrowed by
   * `params.filter` with simulated latency instead of calling the API — driven
   * by the dashboard's floating config panel.
   * @returns Observable of the rows payload with `count` (for the filter) and per-tab `counts`.
   */
  getWaitingOnYouItemsV2(
    params: DashboardWaitingOnYouRequestParamsV2,
    useMock = false,
  ): Observable<WaitingOnYouRowsResponseV2> {
    if (useMock) {
      return of(
        paginateWaitingOnYouMockV2(
          params.filter,
          params.page,
          params.page_size,
        ),
      ).pipe(delay(DASHBOARD_MOCK_DELAY_MS));
    }
    const _url = `${environment.irisApi}/dashboard/waiting-on-you/`;
    const _params = this._buildParams(params);
    return this._httpClient.get<WaitingOnYouRowsResponseV2>(_url, {
      params: _params,
    });
  }

  /**
   * Checks, before navigating, whether a to-do (or any to-do of the
   * submission) is still open and the user can see the submission.
   *
   * @param params - `workspace_id`, plus `task_instance_id` for a single
   * to-do; omit it for the submission-level check.
   * @returns Observable of the check result; a completed to-do is a normal
   * response with `is_open: false`.
   */
  checkWaitingOnYouTodoV2({
    workspace_id,
    task_instance_id,
  }: WaitingOnYouTodoCheckParamsV2): Observable<WaitingOnYouTodoCheckResponseV2> {
    const _url = `${environment.irisApi}/dashboard/waiting-on-you/${workspace_id}/todo-check/`;
    const _params = task_instance_id
      ? new HttpParams().set('task_instance_id', task_instance_id)
      : new HttpParams();
    return this._httpClient.get<WaitingOnYouTodoCheckResponseV2>(_url, {
      params: _params,
    });
  }

  /**
   * Checks, before navigating, that a work order is still Complete with
   * outcome documents the user can see.
   *
   * @param params - `workspace_id` and the clicked `review_id`.
   * @returns Observable of the check result; `is_available: false` means show
   * the toast.
   */
  checkOutcomeDocumentsV2({
    workspace_id,
    review_id,
  }: OutcomeDocumentsCheckParamsV2): Observable<OutcomeDocumentsCheckResponseV2> {
    const _url = `${environment.irisApi}/dashboard/waiting-on-you/${workspace_id}/outcome-documents-check/`;
    const _params = this._buildParams({ review_id });
    return this._httpClient.get<OutcomeDocumentsCheckResponseV2>(_url, {
      params: _params,
    });
  }

  /**
   * Checks, before the "See details" click navigates, that the submission
   * still belongs on the Waiting on you card.
   *
   * @param params - `workspace_id` of the clicked submission.
   * @returns Observable of the check result; the API answers 409 when the
   * submission no longer qualifies, which surfaces as an error.
   */
  checkWaitingOnYouSeeDetailsV2({
    workspace_id,
  }: WaitingOnYouSeeDetailsCheckParamsV2): Observable<WaitingOnYouSeeDetailsCheckResponseV2> {
    const _url = `${environment.irisApi}/dashboard/waiting-on-you/${workspace_id}/see-details-check/`;
    return this._httpClient.get<WaitingOnYouSeeDetailsCheckResponseV2>(_url);
  }

  /**
   *
   * @NOTE: OUTCOMES READY
   */

  /**
   * Fetches outcome documents posted in the last 10 business days, grouped by submission.
   *
   * @param useMock - When true, returns the first 25-document mock page —
   * grouped and individual documents interleaved by `paginateOutcomeReadyMock`
   * — with simulated latency instead of calling the API; driven by the
   * dashboard's floating config panel.
   * @returns Observable of the Outcomes Ready payload (groups, standalone documents, counts).
   */
  getOutcomesReady(
    requestOutComeReadyParams: OutcomeReadyRequestParams = OUTCOME_READY_DEFAULT_REQUEST_PARAMS,
    useMock = false,
  ): Observable<OutcomeReadyResponse> {
    if (useMock) {
      return of(paginateOutcomeReadyMock()).pipe(
        delay(DASHBOARD_MOCK_DELAY_MS),
      );
    }
    const _url = `${environment.irisApi}/dashboard/outcomes-ready/`;
    const _params = this._buildParams(requestOutComeReadyParams);
    return this._httpClient.get<OutcomeReadyResponse>(_url, {
      params: _params,
    });
  }

  /**
   * Requests the binary payload for a single outcome document.
   *
   * `FileService` is reached only through this service so the Outcomes Ready
   * signal state depends on one data service rather than two — the state layer
   * must not inject file/IO services directly.
   *
   * @remarks `FileService.downloadFile` swallows transport errors with
   * `catchError(err => of(err))`, so failures arrive on the `next` channel as
   * error-shaped objects. The return type is therefore `unknown` and callers
   * must prove success from the payload rather than assume it.
   *
   * @param documentId - Identifier of the outcome document to download.
   * @returns Observable emitting the raw `HttpResponse`, or an error-shaped object.
   *
   * @example
   * ```TypeScript
   * this._dashboardService.downloadOutcomeDocument(doc.id).subscribe();
   * ```
   */
  downloadOutcomeDocument(documentId: string): Observable<unknown> {
    return this._fileService.downloadFile(documentId);
  }

  /**
   * Downloads a single outcome document, reporting transfer progress and
   * normalizing every terminal condition into a typed outcome.
   *
   * Replaces the `unknown`-shaped {@link DashboardService.downloadOutcomeDocument}
   * for the Outcomes Ready card: consumers no longer re-derive success by
   * poking at an untyped payload, and a failure always carries a classified
   * reason. The stream **never** errors — transport failures are converted into
   * a `complete` event with a `failure` outcome so a single bad document cannot
   * tear down a `forkJoin` over its whole submission group.
   *
   * @param documentId - Identifier of the outcome document to download.
   * @returns Observable emitting zero or more `progress` events followed by
   * exactly one `complete` event.
   *
   * @example
   * ```TypeScript
   * this._dashboardService.downloadOutcomeDocumentWithProgress(doc.id)
   *   .subscribe(event => {
   *     if (event.kind === 'progress') { render(event.percent); }
   *     else if (event.outcome.status === 'failure') { toast(event.outcome.reason); }
   *   });
   * ```
   */
  downloadOutcomeDocumentWithProgress(
    documentId: string,
  ): Observable<OutcomeDownloadEvent> {
    return this._fileService.downloadFileWithProgress(documentId).pipe(
      filter(
        (event) =>
          event.type === HttpEventType.DownloadProgress ||
          event.type === HttpEventType.Response,
      ),
      map((event): OutcomeDownloadEvent => {
        if (event.type === HttpEventType.DownloadProgress) {
          return {
            kind: 'progress',
            percent: toDownloadPercent(event.loaded, event.total),
          };
        }

        const _response = event as HttpResponse<ArrayBuffer>;

        return _response.ok
          ? {
              kind: 'complete',
              outcome: { status: 'success', response: _response },
            }
          : {
              kind: 'complete',
              outcome: {
                status: 'failure',
                reason: classifyDownloadFailure(_response.status),
                httpStatus: _response.status,
              },
            };
      }),
      catchError((error: unknown) => {
        const _httpStatus =
          error instanceof HttpErrorResponse ? error.status : null;

        return of<OutcomeDownloadEvent>({
          kind: 'complete',
          outcome: {
            status: 'failure',
            reason: classifyDownloadFailure(_httpStatus),
            httpStatus: _httpStatus,
          },
        });
      }),
    );
  }

  /**
   * Zips a submission group's outcome documents, saving the archive and
   * reporting document-count progress as it goes.
   *
   * Raises no toasts of its own — the Outcomes Ready card owns its contractual
   * copy and drives it from the emitted result.
   *
   * @param submission - The submission group being downloaded.
   * @returns Observable of progress events followed by one completion event.
   *
   * @example
   * ```TypeScript
   * this._dashboardService.downloadOutcomeGroupAsZip(group).subscribe(e => {
   *   if (e.type === 'progress') { setPercent(e.completed / e.total); }
   * });
   * ```
   */
  downloadOutcomeGroupAsZip(
    submission: OutcomeReadySubmission,
  ): Observable<ZipProgressEvent> {
    const _files = submission.documents.map((doc) => ({
      id: doc.id,
      title: doc.title,
    }));

    return this._fileService.zipDocuments(
      _files,
      buildOutcomeZipFileName(submission.workspace_name),
    );
  }

  /**
   * Persists a successfully downloaded outcome document to the user's disk.
   *
   * @param response - The successful `HttpResponse` emitted by {@link DashboardService.downloadOutcomeDocument}.
   * @param fileName - Document title used as the saved file name.
   */
  saveOutcomeDocument(response: unknown, fileName: string): void {
    this._fileService.saveFile(response, fileName);
  }

  /**
   * Raises the toast for an outcome-document download result.
   *
   * @param message - Approved product copy from `OUTCOME_READY_TOAST`.
   * @param panelClass - Snackbar panel class from `OUTCOME_READY_SNACKBAR_CLASS`.
   */
  notifyOutcomeDownload(message: string, panelClass: string): void {
    this._fileService.showSnackbar(message, panelClass);
  }

  private _buildParams<T extends Record<string, unknown>>(
    params: T,
  ): HttpParams {
    if (!Object.values(params).length) {
      return new HttpParams();
    }
    let _params = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) {
        continue;
      }
      _params = _params.set(key, `${value}`);
    }
    return _params;
  }
}
